/**
 * DQ Enhancements Panel Component
 *
 * DC-791: Displays enhanced data quality analysis results including:
 * - Disguised missing value detection
 * - Near-duplicate detection (Jaro-Winkler)
 * - Robust outlier detection (MoNNAD + RiLOF consensus)
 *
 * @see Documentation/Development/Design Docs/Missing_Accordion_Capabilities_Design.md
 */

import React, { useState, useRef, useEffect } from 'react';
import './dq-enhancements-panel.css';

// Types matching server/dataQuality/enhancedTypes.ts
type Severity = 'none' | 'low' | 'medium' | 'high';

// DC-1813: execution status rides every report so a check that did not run
// can never render as a clean result.
type ExecutionStatus = 'complete' | 'skipped' | 'partial';

interface ExecutionStatusFields {
  status?: ExecutionStatus;
  skipped_reason?: string;
}

/**
 * DC-1813: "we looked and found nothing" and "we did not look" are different
 * claims. Any module whose status is not 'complete' renders this neutral
 * not-run note in place of its results.
 */
const ModuleNotRunNote: React.FC<{ report: ExecutionStatusFields }> = ({ report }) => (
  <div className="dq-enhance-empty">
    ∅ Not run — {report.skipped_reason || `analysis did not complete (status: ${report.status})`}
  </div>
);

const moduleDidNotRun = (report: ExecutionStatusFields): boolean =>
  report.status !== undefined && report.status !== 'complete';

interface DisguisedMissingColumnResult {
  column: string;
  patterns_detected: Array<{
    pattern: string;
    count: number;
    percentage: number;
  }>;
  total_disguised: number;
  disguised_percentage: number;
  severity: Severity;
  recommendation: string;
}

interface DisguisedMissingReport extends ExecutionStatusFields {
  analyzed_at: string;
  columns_analyzed: number;
  columns_with_disguised: number;
  column_results: DisguisedMissingColumnResult[];
  overall_severity: Severity;
  summary: string;
}

interface NearDuplicateCluster {
  cluster_id: number;
  record_indices: number[];
  representative_index: number;
  avg_intra_similarity: number;
}

interface NearDuplicatePair {
  index1: number;
  index2: number;
  similarity: number;
  matching_columns: string[];
}

interface NearDuplicateResult extends ExecutionStatusFields {
  analyzed_at: string;
  total_records: number;
  comparison_columns: string[];
  similarity_threshold: number;
  duplicate_pairs: NearDuplicatePair[];
  clusters: NearDuplicateCluster[];
  records_in_clusters: number;
  cluster_count: number;
  duplicate_rate: number;
  severity: Severity;
  recommendation: string;
}

interface OutlierPoint {
  index: number;
  value: number;
  monnad_score?: number;
  rilof_score?: number;
  is_outlier_monnad?: boolean;
  is_outlier_rilof?: boolean;
  is_consensus_outlier?: boolean;
}

interface RobustOutlierColumnResult {
  column: string;
  monnad: {
    outlier_count: number;
    outlier_percentage: number;
  };
  rilof: {
    outlier_count: number;
    outlier_percentage: number;
  };
  consensus_outliers: OutlierPoint[];
  consensus_count: number;
  consensus_percentage: number;
  severity: Severity;
  recommendation: string;
}

interface RobustOutlierReport extends ExecutionStatusFields {
  analyzed_at: string;
  columns_analyzed: number;
  columns_with_outliers: number;
  total_consensus_outliers: number;
  column_results: RobustOutlierColumnResult[];
  overall_severity: Severity;
  summary: string;
}

interface DQEnhancementResult {
  disguised_missing?: DisguisedMissingReport;
  near_duplicates_jw?: NearDuplicateResult;
  robust_outliers?: RobustOutlierReport;
  overall_severity: Severity;
  summary: string;
}

interface DQEnhancementsPanelProps {
  journeyId?: string;
  results?: DQEnhancementResult;
  onAnalysisComplete?: (result: DQEnhancementResult) => void;
}

const DQEnhancementsPanel: React.FC<DQEnhancementsPanelProps> = ({
  journeyId,
  results: propResults,
  onAnalysisComplete
}) => {
  const [result, setResult] = useState<DQEnhancementResult | null>(propResults || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'disguised' | 'duplicates' | 'outliers'>('disguised');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (propResults) {
      setResult(propResults);
    }
  }, [propResults]);

  const handleAnalyze = async (file: File) => {
    try {
      setAnalyzing(true);
      setError(null);

      const formData = new FormData();
      formData.append('file', file);
      if (journeyId) {
        formData.append('journey_id', journeyId);
      }

      const response = await fetch('/api/data-quality/enhancements/full', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to run DQ enhancements');
      }

      // Transform response to match our interface
      const enhancementResult: DQEnhancementResult = {
        disguised_missing: data.result.disguised_missing,
        near_duplicates_jw: data.result.near_duplicates,
        robust_outliers: data.result.robust_outliers,
        overall_severity: data.result.overall_severity,
        summary: data.result.summary
      };

      setResult(enhancementResult);
      onAnalysisComplete?.(enhancementResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze');
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityClass = (severity: Severity): string => {
    return `dq-enhance-severity--${severity}`;
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  const toggleSection = (key: string) => {
    setExpandedSection(expandedSection === key ? null : key);
  };

  // Render disguised missing values section
  const renderDisguisedMissing = () => {
    const dm = result?.disguised_missing;
    if (!dm) return <div className="dq-enhance-empty">No disguised missing value analysis available</div>;
    if (moduleDidNotRun(dm)) return <ModuleNotRunNote report={dm} />;

    return (
      <div className="dq-enhance-section">
        <div className="dq-enhance-section__summary">
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{dm.columns_with_disguised}</span>
            <span className="dq-enhance-stat__label">Columns Affected</span>
          </div>
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{dm.columns_analyzed}</span>
            <span className="dq-enhance-stat__label">Columns Analyzed</span>
          </div>
          <div className={`dq-enhance-severity-badge ${getSeverityClass(dm.overall_severity)}`}>
            {dm.overall_severity.toUpperCase()}
          </div>
        </div>

        <p className="dq-enhance-summary-text">{dm.summary}</p>

        {dm.column_results.filter(c => c.total_disguised > 0).length > 0 && (
          <div className="dq-enhance-results-list">
            <h4>Columns with Disguised Missing Values</h4>
            {dm.column_results
              .filter(c => c.total_disguised > 0)
              .map((col, idx) => (
                <div key={idx} className="dq-enhance-result-item">
                  <div
                    className="dq-enhance-result-header"
                    onClick={() => toggleSection(`dm-${col.column}`)}
                  >
                    <span className="dq-enhance-result-name">{col.column}</span>
                    <span className={`dq-enhance-mini-badge ${getSeverityClass(col.severity)}`}>
                      {col.total_disguised} ({formatPercentage(col.disguised_percentage)})
                    </span>
                    <span className="dq-enhance-expand-icon">
                      {expandedSection === `dm-${col.column}` ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedSection === `dm-${col.column}` && (
                    <div className="dq-enhance-result-details">
                      <p className="dq-enhance-recommendation">{col.recommendation}</p>
                      <div className="dq-enhance-patterns">
                        <strong>Detected Patterns:</strong>
                        <ul>
                          {col.patterns_detected.map((p, pIdx) => (
                            <li key={pIdx}>
                              <code>{p.pattern}</code>: {p.count} occurrences ({formatPercentage(p.percentage)})
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  // Render near-duplicate section
  const renderNearDuplicates = () => {
    const nd = result?.near_duplicates_jw;
    if (!nd) return <div className="dq-enhance-empty">No near-duplicate analysis available</div>;
    if (moduleDidNotRun(nd)) return <ModuleNotRunNote report={nd} />;

    return (
      <div className="dq-enhance-section">
        <div className="dq-enhance-section__summary">
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{nd.cluster_count}</span>
            <span className="dq-enhance-stat__label">Duplicate Clusters</span>
          </div>
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{nd.records_in_clusters}</span>
            <span className="dq-enhance-stat__label">Records in Clusters</span>
          </div>
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{formatPercentage(nd.duplicate_rate)}</span>
            <span className="dq-enhance-stat__label">Duplicate Rate</span>
          </div>
          <div className={`dq-enhance-severity-badge ${getSeverityClass(nd.severity)}`}>
            {nd.severity.toUpperCase()}
          </div>
        </div>

        <p className="dq-enhance-summary-text">{nd.recommendation}</p>

        <div className="dq-enhance-config-info">
          <span>Similarity Threshold: {(nd.similarity_threshold * 100).toFixed(0)}%</span>
          <span>Comparison Columns: {nd.comparison_columns.join(', ') || 'Auto-detected'}</span>
        </div>

        {nd.clusters.length > 0 && (
          <div className="dq-enhance-results-list">
            <h4>Duplicate Clusters</h4>
            {nd.clusters.slice(0, 10).map((cluster, idx) => (
              <div key={idx} className="dq-enhance-result-item">
                <div
                  className="dq-enhance-result-header"
                  onClick={() => toggleSection(`nd-${cluster.cluster_id}`)}
                >
                  <span className="dq-enhance-result-name">Cluster {cluster.cluster_id + 1}</span>
                  <span className="dq-enhance-mini-badge dq-enhance-severity--medium">
                    {cluster.record_indices.length} records
                  </span>
                  <span className="dq-enhance-similarity">
                    Avg Similarity: {(cluster.avg_intra_similarity * 100).toFixed(1)}%
                  </span>
                  <span className="dq-enhance-expand-icon">
                    {expandedSection === `nd-${cluster.cluster_id}` ? '▼' : '▶'}
                  </span>
                </div>
                {expandedSection === `nd-${cluster.cluster_id}` && (
                  <div className="dq-enhance-result-details">
                    <p>Record indices in cluster: {cluster.record_indices.slice(0, 20).join(', ')}
                      {cluster.record_indices.length > 20 && ` and ${cluster.record_indices.length - 20} more`}
                    </p>
                    <p>Representative record: Row {cluster.representative_index + 1}</p>
                  </div>
                )}
              </div>
            ))}
            {nd.clusters.length > 10 && (
              <p className="dq-enhance-more-info">
                Showing 10 of {nd.clusters.length} clusters
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render robust outliers section
  const renderRobustOutliers = () => {
    const ro = result?.robust_outliers;
    if (!ro) return <div className="dq-enhance-empty">No robust outlier analysis available</div>;
    if (moduleDidNotRun(ro)) return <ModuleNotRunNote report={ro} />;

    return (
      <div className="dq-enhance-section">
        <div className="dq-enhance-section__summary">
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{ro.total_consensus_outliers}</span>
            <span className="dq-enhance-stat__label">Consensus Outliers</span>
          </div>
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{ro.columns_with_outliers}</span>
            <span className="dq-enhance-stat__label">Columns Affected</span>
          </div>
          <div className="dq-enhance-stat">
            <span className="dq-enhance-stat__value">{ro.columns_analyzed}</span>
            <span className="dq-enhance-stat__label">Columns Analyzed</span>
          </div>
          <div className={`dq-enhance-severity-badge ${getSeverityClass(ro.overall_severity)}`}>
            {ro.overall_severity.toUpperCase()}
          </div>
        </div>

        <p className="dq-enhance-summary-text">{ro.summary}</p>

        <div className="dq-enhance-method-info">
          <div className="dq-enhance-method">
            <strong>MoNNAD</strong>
            <span>Modified Nearest Neighbor Anomaly Detection</span>
          </div>
          <div className="dq-enhance-method">
            <strong>RiLOF</strong>
            <span>Robust Local Outlier Factor</span>
          </div>
        </div>

        {ro.column_results.filter(c => c.consensus_count > 0).length > 0 && (
          <div className="dq-enhance-results-list">
            <h4>Columns with Consensus Outliers</h4>
            {ro.column_results
              .filter(c => c.consensus_count > 0)
              .map((col, idx) => (
                <div key={idx} className="dq-enhance-result-item">
                  <div
                    className="dq-enhance-result-header"
                    onClick={() => toggleSection(`ro-${col.column}`)}
                  >
                    <span className="dq-enhance-result-name">{col.column}</span>
                    <span className={`dq-enhance-mini-badge ${getSeverityClass(col.severity)}`}>
                      {col.consensus_count} consensus ({formatPercentage(col.consensus_percentage)})
                    </span>
                    <span className="dq-enhance-expand-icon">
                      {expandedSection === `ro-${col.column}` ? '▼' : '▶'}
                    </span>
                  </div>
                  {expandedSection === `ro-${col.column}` && (
                    <div className="dq-enhance-result-details">
                      <p className="dq-enhance-recommendation">{col.recommendation}</p>
                      <div className="dq-enhance-method-comparison">
                        <div>
                          <strong>MoNNAD:</strong> {col.monnad.outlier_count} outliers
                          ({formatPercentage(col.monnad.outlier_percentage)})
                        </div>
                        <div>
                          <strong>RiLOF:</strong> {col.rilof.outlier_count} outliers
                          ({formatPercentage(col.rilof.outlier_percentage)})
                        </div>
                      </div>
                      {col.consensus_outliers.length > 0 && (
                        <div className="dq-enhance-outlier-list">
                          <strong>Top Consensus Outliers:</strong>
                          <ul>
                            {col.consensus_outliers.slice(0, 5).map((o, oIdx) => (
                              <li key={oIdx}>
                                Row {o.index + 1}: value = {o.value.toFixed(2)}
                                {o.monnad_score && ` (MoNNAD: ${o.monnad_score.toFixed(2)})`}
                                {o.rilof_score && ` (RiLOF: ${o.rilof_score.toFixed(2)})`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="dq-enhance-panel">
      {/* Header */}
      <div className="dq-enhance-panel__header">
        <div className="dq-enhance-panel__title">
          <h3>Enhanced Data Quality Analysis</h3>
          {result && (
            <span className={`dq-enhance-severity-badge ${getSeverityClass(result.overall_severity)}`}>
              {result.overall_severity.toUpperCase()}
            </span>
          )}
        </div>
        <div className="dq-enhance-panel__actions">
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.tsv,.xlsx,.xls"
            onChange={(e) => e.target.files?.[0] && handleAnalyze(e.target.files[0])}
            style={{ display: 'none' }}
          />
          <button
            className="dq-enhance-btn dq-enhance-btn--primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={analyzing}
          >
            {analyzing ? 'Analyzing...' : 'Analyze File'}
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="dq-enhance-error">
          <span className="dq-enhance-error__icon">⚠</span>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Analyzing indicator */}
      {analyzing && (
        <div className="dq-enhance-loading">
          <div className="dq-enhance-spinner"></div>
          <span>Running enhanced analysis (disguised missing, near-duplicates, robust outliers)...</span>
        </div>
      )}

      {/* Results */}
      {result && !analyzing && (
        <div className="dq-enhance-content">
          {/* Overall summary */}
          <div className="dq-enhance-overall-summary">
            <p>{result.summary}</p>
          </div>

          {/* Tabs */}
          <div className="dq-enhance-tabs">
            <button
              className={`dq-enhance-tab ${activeTab === 'disguised' ? 'dq-enhance-tab--active' : ''}`}
              onClick={() => setActiveTab('disguised')}
            >
              Disguised Missing
              {result.disguised_missing?.columns_with_disguised ? (
                <span className="dq-enhance-tab-badge">{result.disguised_missing.columns_with_disguised}</span>
              ) : null}
            </button>
            <button
              className={`dq-enhance-tab ${activeTab === 'duplicates' ? 'dq-enhance-tab--active' : ''}`}
              onClick={() => setActiveTab('duplicates')}
            >
              Near-Duplicates
              {result.near_duplicates_jw?.cluster_count ? (
                <span className="dq-enhance-tab-badge">{result.near_duplicates_jw.cluster_count}</span>
              ) : null}
            </button>
            <button
              className={`dq-enhance-tab ${activeTab === 'outliers' ? 'dq-enhance-tab--active' : ''}`}
              onClick={() => setActiveTab('outliers')}
            >
              Robust Outliers
              {result.robust_outliers?.total_consensus_outliers ? (
                <span className="dq-enhance-tab-badge">{result.robust_outliers.total_consensus_outliers}</span>
              ) : null}
            </button>
          </div>

          {/* Tab content */}
          <div className="dq-enhance-tab-content">
            {activeTab === 'disguised' && renderDisguisedMissing()}
            {activeTab === 'duplicates' && renderNearDuplicates()}
            {activeTab === 'outliers' && renderRobustOutliers()}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!result && !analyzing && !error && (
        <div className="dq-enhance-empty-state">
          <div className="dq-enhance-empty-icon">📊</div>
          <h4>Enhanced Data Quality Analysis</h4>
          <p>Upload a file to detect:</p>
          <ul>
            <li><strong>Disguised Missing Values</strong> - Placeholder patterns like -999, N/A, null</li>
            <li><strong>Near-Duplicates</strong> - Similar records using Jaro-Winkler similarity</li>
            <li><strong>Robust Outliers</strong> - Anomalies detected by both MoNNAD and RiLOF algorithms</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default DQEnhancementsPanel;
